type NestedKeyOf<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object
          ? K | `${K}.${NestedKeyOf<T[K]>}`
          : K
        : never;
    }[keyof T]
  : never;

type NestedValueOf<T, P extends string> = P extends `${infer K}.${infer Rest}`
  ? K extends keyof T
    ? NestedValueOf<T[K], Rest>
    : never
  : P extends keyof T
    ? T[P]
    : never;

export function updateNested<T extends object, P extends NestedKeyOf<T>>(
  obj: T,
  path: P,
  value: NestedValueOf<T, P>,
): T {
  const keys = (path as string).split(".");
  const [first, ...rest] = keys;
  if (rest.length === 0) return { ...obj, [first]: value };
  return {
    ...obj,
    [first]: updateNested((obj as any)[first], rest.join(".") as any, value),
  };
}

export function updateNestedArray<T extends object, P extends NestedKeyOf<T>>(
  obj: T,
  path: P,
  index: number,
  value: NestedValueOf<T, P> extends Array<infer Item> ? Partial<Item> : never,
): T {
  const arr = getNestedValue(obj, path) as any[];
  const newArr = arr.map((item, i) =>
    i === index ? { ...item, ...(value as any) } : item,
  );
  return updateNested(obj, path, newArr as any);
}

export function getNestedValue<T extends object, P extends NestedKeyOf<T>>(
  obj: T,
  path: P,
): NestedValueOf<T, P> {
  const keys = (path as string).split(".");
  return keys.reduce((current, key) => (current as any)[key], obj as any);
}
